import { useTranslation } from "react-i18next";
import useAxios from "../../../hooks/useAxios";
import { IGetStudentPayments, IStudentPayment } from "../../../models/payments.model";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import useNotification from "../../../hooks/useNotification";
import { postLog } from "../../../api/log.api";
import _ from "lodash";
import { getStudentPayments } from "../../../api/payments.api";
import { IParent, IStudent } from "../../../models/students.model";
import useLoader from "../../../hooks/useLoader";
import StudentInfoTable from "./payment/StudentInfoTable";
import PaymentToBeMade from "./payment/PaymentToBeMade";
import PaymentMade from "./payment/PaymentMade";
import PaymentProcessing from "./payment/PaymentProcessing";
import AddPaymentModal from "./modals/AddPaymentModal";
import DownloadCertificateModal from "./modals/DownloadCertificateModal";

export enum EPaymentMode {
  PaymentToBeMade,
  PaymentMade,
  PaymentProcessing,
};


export enum EPaymentStep {
  BeforePayment = 0,
  FirstPayment = 1,
  SecondPayment = 2,
  ThirdPayment = 3,
  PaymentCompleted = 4
};

interface IProps {
  student?: IStudent;
  parent?: IParent;
  setTabIndex: (tabIndex: number) => void;
};

const StudentPaymentPane: FC<IProps> = ({
  student,
  parent,
  setTabIndex,
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const { execute: logExecute } = useAxios();
  const { data: res, isLoading, error, execute } = useAxios<IGetStudentPayments>();
  useLoader(isLoading, [error]);

  const [paymentMode, setPaymentMode] = useState<EPaymentMode>(EPaymentMode.PaymentMade);
  const [paymentStep, setPaymentStep] = useState<EPaymentStep>(EPaymentStep.BeforePayment);
  const [studentPayments, setStudentPayments] = useState<IStudentPayment[]>([]);
  const [addPaymentVisible, setAddPaymentVisible] = useState(false);
  const [downloadCertificateVisible, setDownloadCertificateVisible] = useState(false);
  const [isReload, setIsReload] = useState(false);

  useEffect(() => {
    if(!isReload) {
      doGetStudentPayments();
    } else {
      setIsReload(false);
    }
  }, [student, isReload]);

  useEffect(() => {
    if (res?.data) {
      const newStudentPayments = _.sortBy(
        res.data,
        item => item.paymentCloseDate,
      );
      setStudentPayments(newStudentPayments);
    }
  }, [res]);

  const doGetStudentPayments = () => {
    if (student?.studentCode) {
      execute(getStudentPayments(student.studentCode));
    }
  };

  const onSetPaymentMode = (newMode: EPaymentMode) => {
    // if (mode === 'acceptance' && !isExtended) {
    //   onIsExtendedChange();
    // }
    if (
      newMode === EPaymentMode.PaymentProcessing &&
      !res?.data.length
    ) {
      return infoNotification(t('student.student.message25'));
    }

    setPaymentMode(newMode);
  };

  const renderPaymentMode = () => {
    switch (paymentMode) {
      case EPaymentMode.PaymentToBeMade:
        return (
          <PaymentToBeMade
            data={studentPayments}
            paymentMode={paymentMode}
            onSetPaymentMode={onSetPaymentMode}
            setAddPaymentVisible={setAddPaymentVisible}
            setDownloadCertificateVisible={setDownloadCertificateVisible}
          />
        );
      case EPaymentMode.PaymentMade:
        return (
          <PaymentMade
            student={student}
            paymentMode={paymentMode}
            onSetPaymentMode={onSetPaymentMode}
            setAddPaymentVisible={setAddPaymentVisible}
            setDownloadCertificateVisible={setDownloadCertificateVisible}
            sendLog={sendLog}
            setIsReload={setIsReload}
            setTabIndex={setTabIndex}
          />
        );
      case EPaymentMode.PaymentProcessing:
        return (
          <PaymentProcessing
            student={student}
            parent={parent}
            onSetPaymentMode={onSetPaymentMode}
            paymentStep={paymentStep}
            setPaymentStep={setPaymentStep}
            studentPayments={studentPayments}
            doGetStudentPayments={doGetStudentPayments}
            sendLog={sendLog}
          />
        );      
    }
  };

  const sendLog = (
    message: string,
    object: object = {},
  ) => {
    logExecute(postLog({
      webId: auth.webId,
      korName: auth.korName,
      paymentApi: message,
      ...object,
    }));
  };

  return (
    <>
      <StudentInfoTable
        student={student}
        paymentStep={paymentStep}
      />
      {renderPaymentMode()}
      
      {addPaymentVisible &&
        <AddPaymentModal
          visible={addPaymentVisible}
          setVisible={setAddPaymentVisible}
          memberCode={student?.studentCode}
          doGetStudentPayments={doGetStudentPayments}
          sendLog={sendLog}
        />
      }
      {downloadCertificateVisible &&
        <DownloadCertificateModal
          visible={downloadCertificateVisible}
          setVisible={setDownloadCertificateVisible}
          student={student}
        />
      }
    </>
  );
};

export default StudentPaymentPane;